import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig.js";
import { toast } from "react-toastify";
import LoginHistoryModal from "./LoginHistoryModal.js";
import "./LoginActivity.css";

const API_URL = process.env.REACT_APP_API_URL;

const LoginActivity = () => {
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchActiveUsers();
  }, []);

  const fetchActiveUsers = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API_URL}/api/users/active`);
      setActiveUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching active users:", error);
      toast.error("Fehler beim Laden der aktiven Benutzer");
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  };

  const getActivityStatus = (lastActivity) => {
    if (!lastActivity) return { icon: "🔴", text: "Keine Aktivität", color: "#999" };

    const now = new Date();
    const activityDate = new Date(lastActivity);
    const minutesAgo = Math.floor((now - activityDate) / 1000 / 60);

    if (minutesAgo < 5) {
      return { icon: "🟢", text: "Aktiv", color: "#4caf50" };
    } else if (minutesAgo < 60) {
      return { icon: "🟡", text: "Kürzlich aktiv", color: "#ff9800" };
    } else {
      return { icon: "🔴", text: "Inaktiv", color: "#999" };
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);
    const now = new Date();

    // Format time
    const timeStr = date.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit"
    });

    // Check if today
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return `Heute, ${timeStr}`;
    } else {
      const dateStr = date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit"
      });
      return `${dateStr}, ${timeStr}`;
    }
  };

  if (loading) {
    return (
      <div className="login-activity-container">
        <h1>📊 Login-Aktivität</h1>
        <p>Lädt...</p>
      </div>
    );
  }

  return (
    <div className="login-activity-container">
      <div className="login-activity-header">
        <h1>📊 Login-Aktivität</h1>
        <p className="login-activity-description">
          Benutzer, die in den letzten 60 Minuten aktiv waren
        </p>
      </div>

      <div className="login-activity-actions">
        <button
          className="refresh-button"
          onClick={fetchActiveUsers}
          disabled={refreshing}
        >
          {refreshing ? "Aktualisiert..." : "🔄 Aktualisieren"}
        </button>
      </div>

      {activeUsers.length === 0 ? (
        <div className="no-users-message">
          <p>Keine aktiven Benutzer gefunden</p>
          <span className="hint">
            Benutzer werden als aktiv angezeigt, wenn sie in den letzten 60 Minuten eine Aktion durchgeführt haben
          </span>
        </div>
      ) : (
        <div className="activity-table-container">
          <table className="activity-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Name</th>
                <th>Letzte Aktivität</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {activeUsers.map((user) => {
                const status = getActivityStatus(user.lastActivity);
                return (
                  <tr key={user._id}>
                    <td>
                      <span
                        className="status-indicator"
                        style={{ color: status.color }}
                      >
                        {status.icon} {status.text}
                      </span>
                    </td>
                    <td className="user-name">
                      {user.firstName} {user.lastName}
                    </td>
                    <td className="activity-time">
                      {formatTime(user.lastActivity)}
                    </td>
                    <td>
                      <button
                        className="history-button"
                        onClick={() => setSelectedUser(user)}
                        title="Login-Historie anzeigen"
                      >
                        📜 Historie
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="activity-summary">
            <p>
              {activeUsers.length} {activeUsers.length === 1 ? "Benutzer" : "Benutzer"} aktiv
            </p>
            <div className="legend">
              <span className="legend-item">
                <span style={{ color: "#4caf50" }}>🟢 Aktiv</span> - Letzte 5 Minuten
              </span>
              <span className="legend-item">
                <span style={{ color: "#ff9800" }}>🟡 Kürzlich aktiv</span> - Letzte 60 Minuten
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Login History Modal */}
      {selectedUser && (
        <LoginHistoryModal
          userId={selectedUser._id}
          userName={`${selectedUser.firstName} ${selectedUser.lastName}`}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
};

export default LoginActivity;
